// Single source of truth for the Country list and what it defaults the
// Timezone and Locale pickers to. The UI language never changes — it's
// always English — but the *locale* code still matters on its own: it's
// what Intl.DateTimeFormat/NumberFormat use to decide date order, decimal
// separators, etc. (see docs_zenanalytics, locale/formatting pass
// 2026-10-20). Country only sets the *default* for timezone and locale;
// both stay freely editable right next to it, since plenty of people live
// in one country and are temporarily in another timezone.
//
// Timezone picks the country's single zone where there is one, or its
// most populous/capital zone where the country spans several (e.g. the
// US -> America/New_York, Australia -> Australia/Sydney, Russia ->
// Europe/Moscow) — a sensible default, not a claim that it's the only
// correct zone for that country.

export const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Andorra','Angola','Antigua and Barbuda','Argentina','Armenia',
  'Australia','Austria','Azerbaijan','Bahamas','Bahrain','Bangladesh','Barbados','Belarus','Belgium',
  'Belize','Benin','Bhutan','Bolivia','Bosnia and Herzegovina','Botswana','Brazil','Brunei',
  'Bulgaria','Burkina Faso','Burundi','Cabo Verde','Cambodia','Cameroon','Canada',
  'Central African Republic','Chad','Chile','China','Colombia','Comoros','Congo','Costa Rica',
  'Croatia','Cuba','Cyprus','Czech Republic','Denmark','Djibouti','Dominica','Dominican Republic',
  'Ecuador','Egypt','El Salvador','Equatorial Guinea','Eritrea','Estonia','Eswatini','Ethiopia',
  'Fiji','Finland','France','Gabon','Gambia','Georgia','Germany','Ghana','Greece','Grenada',
  'Guatemala','Guinea','Guinea-Bissau','Guyana','Haiti','Honduras','Hungary','Iceland','India',
  'Indonesia','Iran','Iraq','Ireland','Israel','Italy','Jamaica','Japan','Jordan','Kazakhstan',
  'Kenya','Kiribati','Kuwait','Kyrgyzstan','Laos','Latvia','Lebanon','Lesotho','Liberia','Libya',
  'Liechtenstein','Lithuania','Luxembourg','Madagascar','Malawi','Malaysia','Maldives','Mali',
  'Malta','Marshall Islands','Mauritania','Mauritius','Mexico','Micronesia','Moldova','Monaco',
  'Mongolia','Montenegro','Morocco','Mozambique','Myanmar','Namibia','Nauru','Nepal','Netherlands',
  'New Zealand','Nicaragua','Niger','Nigeria','North Korea','North Macedonia','Norway','Oman',
  'Pakistan','Palau','Panama','Papua New Guinea','Paraguay','Peru','Philippines','Poland',
  'Portugal','Qatar','Romania','Russia','Rwanda','Saint Kitts and Nevis','Saint Lucia',
  'Saint Vincent and the Grenadines','Samoa','San Marino','Sao Tome and Principe','Saudi Arabia',
  'Senegal','Serbia','Seychelles','Sierra Leone','Singapore','Slovakia','Slovenia',
  'Solomon Islands','Somalia','South Africa','South Korea','South Sudan','Spain','Sri Lanka',
  'Sudan','Suriname','Sweden','Switzerland','Syria','Taiwan','Tajikistan','Tanzania','Thailand',
  'Timor-Leste','Togo','Tonga','Trinidad and Tobago','Tunisia','Turkey','Turkmenistan','Tuvalu',
  'Uganda','Ukraine','United Arab Emirates','United Kingdom','United States','Uruguay','Uzbekistan',
  'Vanuatu','Vatican City','Venezuela','Vietnam','Yemen','Zambia','Zimbabwe'
];

// name -> { timezone: IANA zone, locale: BCP-47 tag }
const DEFAULTS = {
  'Afghanistan': { timezone: 'Asia/Kabul', locale: 'en-GB' },
  'Albania': { timezone: 'Europe/Tirane', locale: 'sq-AL' },
  'Algeria': { timezone: 'Africa/Algiers', locale: 'ar-DZ' },
  'Andorra': { timezone: 'Europe/Andorra', locale: 'ca-AD' },
  'Angola': { timezone: 'Africa/Luanda', locale: 'pt-AO' },
  'Antigua and Barbuda': { timezone: 'America/Antigua', locale: 'en-AG' },
  'Argentina': { timezone: 'America/Argentina/Buenos_Aires', locale: 'es-AR' },
  'Armenia': { timezone: 'Asia/Yerevan', locale: 'hy-AM' },
  'Australia': { timezone: 'Australia/Sydney', locale: 'en-AU' },
  'Austria': { timezone: 'Europe/Vienna', locale: 'de-AT' },
  'Azerbaijan': { timezone: 'Asia/Baku', locale: 'az-AZ' },
  'Bahamas': { timezone: 'America/Nassau', locale: 'en-BS' },
  'Bahrain': { timezone: 'Asia/Bahrain', locale: 'ar-BH' },
  'Bangladesh': { timezone: 'Asia/Dhaka', locale: 'bn-BD' },
  'Barbados': { timezone: 'America/Barbados', locale: 'en-BB' },
  'Belarus': { timezone: 'Europe/Minsk', locale: 'be-BY' },
  'Belgium': { timezone: 'Europe/Brussels', locale: 'nl-BE' },
  'Belize': { timezone: 'America/Belize', locale: 'en-BZ' },
  'Benin': { timezone: 'Africa/Porto-Novo', locale: 'fr-BJ' },
  'Bhutan': { timezone: 'Asia/Thimphu', locale: 'en-GB' },
  'Bolivia': { timezone: 'America/La_Paz', locale: 'es-BO' },
  'Bosnia and Herzegovina': { timezone: 'Europe/Sarajevo', locale: 'bs-BA' },
  'Botswana': { timezone: 'Africa/Gaborone', locale: 'en-BW' },
  'Brazil': { timezone: 'America/Sao_Paulo', locale: 'pt-BR' },
  'Brunei': { timezone: 'Asia/Brunei', locale: 'ms-BN' },
  'Bulgaria': { timezone: 'Europe/Sofia', locale: 'bg-BG' },
  'Burkina Faso': { timezone: 'Africa/Ouagadougou', locale: 'fr-BF' },
  'Burundi': { timezone: 'Africa/Bujumbura', locale: 'fr-BI' },
  'Cabo Verde': { timezone: 'Atlantic/Cape_Verde', locale: 'pt-CV' },
  'Cambodia': { timezone: 'Asia/Phnom_Penh', locale: 'km-KH' },
  'Cameroon': { timezone: 'Africa/Douala', locale: 'fr-CM' },
  'Canada': { timezone: 'America/Toronto', locale: 'en-CA' },
  'Central African Republic': { timezone: 'Africa/Bangui', locale: 'fr-CF' },
  'Chad': { timezone: 'Africa/Ndjamena', locale: 'fr-TD' },
  'Chile': { timezone: 'America/Santiago', locale: 'es-CL' },
  'China': { timezone: 'Asia/Shanghai', locale: 'zh-CN' },
  'Colombia': { timezone: 'America/Bogota', locale: 'es-CO' },
  'Comoros': { timezone: 'Indian/Comoro', locale: 'fr-KM' },
  'Congo': { timezone: 'Africa/Brazzaville', locale: 'fr-CG' },
  'Costa Rica': { timezone: 'America/Costa_Rica', locale: 'es-CR' },
  'Croatia': { timezone: 'Europe/Zagreb', locale: 'hr-HR' },
  'Cuba': { timezone: 'America/Havana', locale: 'es-CU' },
  'Cyprus': { timezone: 'Asia/Nicosia', locale: 'el-CY' },
  'Czech Republic': { timezone: 'Europe/Prague', locale: 'cs-CZ' },
  'Denmark': { timezone: 'Europe/Copenhagen', locale: 'da-DK' },
  'Djibouti': { timezone: 'Africa/Djibouti', locale: 'fr-DJ' },
  'Dominica': { timezone: 'America/Dominica', locale: 'en-DM' },
  'Dominican Republic': { timezone: 'America/Santo_Domingo', locale: 'es-DO' },
  'Ecuador': { timezone: 'America/Guayaquil', locale: 'es-EC' },
  'Egypt': { timezone: 'Africa/Cairo', locale: 'ar-EG' },
  'El Salvador': { timezone: 'America/El_Salvador', locale: 'es-SV' },
  'Equatorial Guinea': { timezone: 'Africa/Malabo', locale: 'es-GQ' },
  'Eritrea': { timezone: 'Africa/Asmara', locale: 'en-ER' },
  'Estonia': { timezone: 'Europe/Tallinn', locale: 'et-EE' },
  'Eswatini': { timezone: 'Africa/Mbabane', locale: 'en-SZ' },
  'Ethiopia': { timezone: 'Africa/Addis_Ababa', locale: 'en-ET' },
  'Fiji': { timezone: 'Pacific/Fiji', locale: 'en-FJ' },
  'Finland': { timezone: 'Europe/Helsinki', locale: 'fi-FI' },
  'France': { timezone: 'Europe/Paris', locale: 'fr-FR' },
  'Gabon': { timezone: 'Africa/Libreville', locale: 'fr-GA' },
  'Gambia': { timezone: 'Africa/Banjul', locale: 'en-GM' },
  'Georgia': { timezone: 'Asia/Tbilisi', locale: 'ka-GE' },
  'Germany': { timezone: 'Europe/Berlin', locale: 'de-DE' },
  'Ghana': { timezone: 'Africa/Accra', locale: 'en-GH' },
  'Greece': { timezone: 'Europe/Athens', locale: 'el-GR' },
  'Grenada': { timezone: 'America/Grenada', locale: 'en-GD' },
  'Guatemala': { timezone: 'America/Guatemala', locale: 'es-GT' },
  'Guinea': { timezone: 'Africa/Conakry', locale: 'fr-GN' },
  'Guinea-Bissau': { timezone: 'Africa/Bissau', locale: 'pt-GW' },
  'Guyana': { timezone: 'America/Guyana', locale: 'en-GY' },
  'Haiti': { timezone: 'America/Port-au-Prince', locale: 'fr-HT' },
  'Honduras': { timezone: 'America/Tegucigalpa', locale: 'es-HN' },
  'Hungary': { timezone: 'Europe/Budapest', locale: 'hu-HU' },
  'Iceland': { timezone: 'Atlantic/Reykjavik', locale: 'is-IS' },
  'India': { timezone: 'Asia/Kolkata', locale: 'en-IN' },
  'Indonesia': { timezone: 'Asia/Jakarta', locale: 'id-ID' },
  'Iran': { timezone: 'Asia/Tehran', locale: 'fa-IR' },
  'Iraq': { timezone: 'Asia/Baghdad', locale: 'ar-IQ' },
  'Ireland': { timezone: 'Europe/Dublin', locale: 'en-IE' },
  'Israel': { timezone: 'Asia/Jerusalem', locale: 'he-IL' },
  'Italy': { timezone: 'Europe/Rome', locale: 'it-IT' },
  'Jamaica': { timezone: 'America/Jamaica', locale: 'en-JM' },
  'Japan': { timezone: 'Asia/Tokyo', locale: 'ja-JP' },
  'Jordan': { timezone: 'Asia/Amman', locale: 'ar-JO' },
  'Kazakhstan': { timezone: 'Asia/Almaty', locale: 'kk-KZ' },
  'Kenya': { timezone: 'Africa/Nairobi', locale: 'en-KE' },
  'Kiribati': { timezone: 'Pacific/Tarawa', locale: 'en-KI' },
  'Kuwait': { timezone: 'Asia/Kuwait', locale: 'ar-KW' },
  'Kyrgyzstan': { timezone: 'Asia/Bishkek', locale: 'ky-KG' },
  'Laos': { timezone: 'Asia/Vientiane', locale: 'lo-LA' },
  'Latvia': { timezone: 'Europe/Riga', locale: 'lv-LV' },
  'Lebanon': { timezone: 'Asia/Beirut', locale: 'ar-LB' },
  'Lesotho': { timezone: 'Africa/Maseru', locale: 'en-LS' },
  'Liberia': { timezone: 'Africa/Monrovia', locale: 'en-LR' },
  'Libya': { timezone: 'Africa/Tripoli', locale: 'ar-LY' },
  'Liechtenstein': { timezone: 'Europe/Vaduz', locale: 'de-LI' },
  'Lithuania': { timezone: 'Europe/Vilnius', locale: 'lt-LT' },
  'Luxembourg': { timezone: 'Europe/Luxembourg', locale: 'fr-LU' },
  'Madagascar': { timezone: 'Indian/Antananarivo', locale: 'fr-MG' },
  'Malawi': { timezone: 'Africa/Blantyre', locale: 'en-MW' },
  'Malaysia': { timezone: 'Asia/Kuala_Lumpur', locale: 'ms-MY' },
  'Maldives': { timezone: 'Indian/Maldives', locale: 'en-GB' },
  'Mali': { timezone: 'Africa/Bamako', locale: 'fr-ML' },
  'Malta': { timezone: 'Europe/Malta', locale: 'en-MT' },
  'Marshall Islands': { timezone: 'Pacific/Majuro', locale: 'en-MH' },
  'Mauritania': { timezone: 'Africa/Nouakchott', locale: 'ar-MR' },
  'Mauritius': { timezone: 'Indian/Mauritius', locale: 'en-MU' },
  'Mexico': { timezone: 'America/Mexico_City', locale: 'es-MX' },
  'Micronesia': { timezone: 'Pacific/Pohnpei', locale: 'en-FM' },
  'Moldova': { timezone: 'Europe/Chisinau', locale: 'ro-MD' },
  'Monaco': { timezone: 'Europe/Monaco', locale: 'fr-MC' },
  'Mongolia': { timezone: 'Asia/Ulaanbaatar', locale: 'mn-MN' },
  'Montenegro': { timezone: 'Europe/Podgorica', locale: 'sr-ME' },
  'Morocco': { timezone: 'Africa/Casablanca', locale: 'ar-MA' },
  'Mozambique': { timezone: 'Africa/Maputo', locale: 'pt-MZ' },
  'Myanmar': { timezone: 'Asia/Yangon', locale: 'en-GB' },
  'Namibia': { timezone: 'Africa/Windhoek', locale: 'en-NA' },
  'Nauru': { timezone: 'Pacific/Nauru', locale: 'en-NR' },
  'Nepal': { timezone: 'Asia/Kathmandu', locale: 'ne-NP' },
  'Netherlands': { timezone: 'Europe/Amsterdam', locale: 'nl-NL' },
  'New Zealand': { timezone: 'Pacific/Auckland', locale: 'en-NZ' },
  'Nicaragua': { timezone: 'America/Managua', locale: 'es-NI' },
  'Niger': { timezone: 'Africa/Niamey', locale: 'fr-NE' },
  'Nigeria': { timezone: 'Africa/Lagos', locale: 'en-NG' },
  'North Korea': { timezone: 'Asia/Pyongyang', locale: 'en-GB' },
  'North Macedonia': { timezone: 'Europe/Skopje', locale: 'mk-MK' },
  'Norway': { timezone: 'Europe/Oslo', locale: 'nb-NO' },
  'Oman': { timezone: 'Asia/Muscat', locale: 'ar-OM' },
  'Pakistan': { timezone: 'Asia/Karachi', locale: 'en-PK' },
  'Palau': { timezone: 'Pacific/Palau', locale: 'en-PW' },
  'Panama': { timezone: 'America/Panama', locale: 'es-PA' },
  'Papua New Guinea': { timezone: 'Pacific/Port_Moresby', locale: 'en-PG' },
  'Paraguay': { timezone: 'America/Asuncion', locale: 'es-PY' },
  'Peru': { timezone: 'America/Lima', locale: 'es-PE' },
  'Philippines': { timezone: 'Asia/Manila', locale: 'en-PH' },
  'Poland': { timezone: 'Europe/Warsaw', locale: 'pl-PL' },
  'Portugal': { timezone: 'Europe/Lisbon', locale: 'pt-PT' },
  'Qatar': { timezone: 'Asia/Qatar', locale: 'ar-QA' },
  'Romania': { timezone: 'Europe/Bucharest', locale: 'ro-RO' },
  'Russia': { timezone: 'Europe/Moscow', locale: 'ru-RU' },
  'Rwanda': { timezone: 'Africa/Kigali', locale: 'en-RW' },
  'Saint Kitts and Nevis': { timezone: 'America/St_Kitts', locale: 'en-KN' },
  'Saint Lucia': { timezone: 'America/St_Lucia', locale: 'en-LC' },
  'Saint Vincent and the Grenadines': { timezone: 'America/St_Vincent', locale: 'en-VC' },
  'Samoa': { timezone: 'Pacific/Apia', locale: 'en-WS' },
  'San Marino': { timezone: 'Europe/San_Marino', locale: 'it-SM' },
  'Sao Tome and Principe': { timezone: 'Africa/Sao_Tome', locale: 'pt-ST' },
  'Saudi Arabia': { timezone: 'Asia/Riyadh', locale: 'ar-SA' },
  'Senegal': { timezone: 'Africa/Dakar', locale: 'fr-SN' },
  'Serbia': { timezone: 'Europe/Belgrade', locale: 'sr-RS' },
  'Seychelles': { timezone: 'Indian/Mahe', locale: 'fr-SC' },
  'Sierra Leone': { timezone: 'Africa/Freetown', locale: 'en-SL' },
  'Singapore': { timezone: 'Asia/Singapore', locale: 'en-SG' },
  'Slovakia': { timezone: 'Europe/Bratislava', locale: 'sk-SK' },
  'Slovenia': { timezone: 'Europe/Ljubljana', locale: 'sl-SI' },
  'Solomon Islands': { timezone: 'Pacific/Guadalcanal', locale: 'en-SB' },
  'Somalia': { timezone: 'Africa/Mogadishu', locale: 'en-SO' },
  'South Africa': { timezone: 'Africa/Johannesburg', locale: 'en-ZA' },
  'South Korea': { timezone: 'Asia/Seoul', locale: 'ko-KR' },
  'South Sudan': { timezone: 'Africa/Juba', locale: 'en-SS' },
  'Spain': { timezone: 'Europe/Madrid', locale: 'es-ES' },
  'Sri Lanka': { timezone: 'Asia/Colombo', locale: 'en-LK' },
  'Sudan': { timezone: 'Africa/Khartoum', locale: 'ar-SD' },
  'Suriname': { timezone: 'America/Paramaribo', locale: 'nl-SR' },
  'Sweden': { timezone: 'Europe/Stockholm', locale: 'sv-SE' },
  'Switzerland': { timezone: 'Europe/Zurich', locale: 'de-CH' },
  'Syria': { timezone: 'Asia/Damascus', locale: 'ar-SY' },
  'Taiwan': { timezone: 'Asia/Taipei', locale: 'zh-TW' },
  'Tajikistan': { timezone: 'Asia/Dushanbe', locale: 'tg-TJ' },
  'Tanzania': { timezone: 'Africa/Dar_es_Salaam', locale: 'en-TZ' },
  'Thailand': { timezone: 'Asia/Bangkok', locale: 'th-TH' },
  'Timor-Leste': { timezone: 'Asia/Dili', locale: 'pt-TL' },
  'Togo': { timezone: 'Africa/Lome', locale: 'fr-TG' },
  'Tonga': { timezone: 'Pacific/Tongatapu', locale: 'en-TO' },
  'Trinidad and Tobago': { timezone: 'America/Port_of_Spain', locale: 'en-TT' },
  'Tunisia': { timezone: 'Africa/Tunis', locale: 'ar-TN' },
  'Turkey': { timezone: 'Europe/Istanbul', locale: 'tr-TR' },
  'Turkmenistan': { timezone: 'Asia/Ashgabat', locale: 'tk-TM' },
  'Tuvalu': { timezone: 'Pacific/Funafuti', locale: 'en-TV' },
  'Uganda': { timezone: 'Africa/Kampala', locale: 'en-UG' },
  'Ukraine': { timezone: 'Europe/Kyiv', locale: 'uk-UA' },
  'United Arab Emirates': { timezone: 'Asia/Dubai', locale: 'ar-AE' },
  'United Kingdom': { timezone: 'Europe/London', locale: 'en-GB' },
  'United States': { timezone: 'America/New_York', locale: 'en-US' },
  'Uruguay': { timezone: 'America/Montevideo', locale: 'es-UY' },
  'Uzbekistan': { timezone: 'Asia/Tashkent', locale: 'uz-UZ' },
  'Vanuatu': { timezone: 'Pacific/Efate', locale: 'en-VU' },
  'Vatican City': { timezone: 'Europe/Vatican', locale: 'it-VA' },
  'Venezuela': { timezone: 'America/Caracas', locale: 'es-VE' },
  'Vietnam': { timezone: 'Asia/Ho_Chi_Minh', locale: 'vi-VN' },
  'Yemen': { timezone: 'Asia/Aden', locale: 'ar-YE' },
  'Zambia': { timezone: 'Africa/Lusaka', locale: 'en-ZM' },
  'Zimbabwe': { timezone: 'Africa/Harare', locale: 'en-ZW' },
};

// Returns { timezone, locale } for a country name from COUNTRIES, or null
// if the name isn't recognized (caller should leave existing fields as-is).
export function countryDefaults(countryName) {
  return DEFAULTS[countryName] || null;
}
